import React from 'react';
import { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  Paper,
  Button,
  Alert,
  Divider
} from '@mui/material';
import kzmSettingsService, { KzmSettingsDto } from '../../services/kzmSettingsService';

interface KzmSettingsProps {
  projectId: string;
  initialJson?: string;
}

type Person = { name: string; email: string };

interface KzmSettingsState {
  orderingParty: Person; // Zamawiający
  inspectorBuild: Person; // Nadzór budowlany
  inspectorSanitary: Person; // Nadzór sanitarny
  inspectorElectrical: Person; // Nadzór elektryczny
  inspectorTelecom: Person; // Nadzór teletechniczny
  ccBuild: string; // DW budowlana (comma separated)
  ccSanitary: string; // DW sanitarna
  ccElectrical: string; // DW elektryczna
  ccTelecom: string; // DW teletechniczna
  templateLink?: string; // Link do szablonu KZM
}

const DEFAULT_STATE: KzmSettingsState = {
  orderingParty: { name: '', email: '' },
  inspectorBuild: { name: '', email: '' },
  inspectorSanitary: { name: '', email: '' },
  inspectorElectrical: { name: '', email: '' },
  inspectorTelecom: { name: '', email: '' },
  ccBuild: '',
  ccSanitary: '',
  ccElectrical: '',
  ccTelecom: '',
  templateLink: ''
};

function parseInitial(initialJson?: string): KzmSettingsState {
  if (!initialJson) return { ...DEFAULT_STATE };
  try {
    const parsed = JSON.parse(initialJson);
    return {
      orderingParty: parsed.orderingParty || { name: '', email: '' },
      inspectorBuild: parsed.inspectorBuild || { name: '', email: '' },
      inspectorSanitary: parsed.inspectorSanitary || { name: '', email: '' },
      inspectorElectrical: parsed.inspectorElectrical || { name: '', email: '' },
      inspectorTelecom: parsed.inspectorTelecom || { name: '', email: '' },
      ccBuild: parsed.ccBuild || '',
      ccSanitary: parsed.ccSanitary || '',
      ccElectrical: parsed.ccElectrical || '',
      ccTelecom: parsed.ccTelecom || '',
      templateLink: parsed.templateLink || ''
    } as KzmSettingsState;
  } catch {
    return { ...DEFAULT_STATE };
  }
}

const emailRegex = /^(?:\s*[\w.!#$%&'*+/=?^`{|}~-]+@[\w-]+(?:\.[\w-]+)+\s*)(?:,\s*[\w.!#$%&'*+/=?^`{|}~-]+@[\w-]+(?:\.[\w-]+)+\s*)*$/i;

const KzmSettings: React.FC<KzmSettingsProps> = ({ projectId, initialJson }) => {
  const [state, setState] = useState<KzmSettingsState>(() => ({ ...parseInitial(initialJson), templateLink: 'https://docs.google.com/document/d/1cl6VKFX_nhWgujm1mhsgYN1XQsxXSl2oXWIKqcn3kmo/edit?tab=t.0' }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Load from API
  React.useEffect(() => {
    (async () => {
      try {
        const data = await kzmSettingsService.getKzmSettings(projectId);
        if (data) {
          const next: KzmSettingsState = {
            orderingParty: { name: data.ordering_party_name || '', email: data.ordering_party_email || '' },
            inspectorBuild: { name: data.inspector_build_name || '', email: data.inspector_build_email || '' },
            inspectorSanitary: { name: data.inspector_sanitary_name || '', email: data.inspector_sanitary_email || '' },
            inspectorElectrical: { name: data.inspector_electrical_name || '', email: data.inspector_electrical_email || '' },
            inspectorTelecom: { name: data.inspector_telecom_name || '', email: data.inspector_telecom_email || '' },
            ccBuild: data.cc_build || '',
            ccSanitary: data.cc_sanitary || '',
            ccElectrical: data.cc_electrical || '',
            ccTelecom: data.cc_telecom || '',
            templateLink: data.template_link || ''
          };
          setState(next);
        }
      } catch (e: any) {
        setError(e?.message || 'Nie udało się pobrać ustawień KZM');
      }
    })();
  }, [projectId]);

  const isEmailsValid = useMemo(() => {
    const all = [state.orderingParty.email, state.inspectorBuild.email, state.inspectorSanitary.email, state.inspectorElectrical.email, state.inspectorTelecom.email]
      .filter(Boolean)
      .join(',');
    const cc = [state.ccBuild, state.ccSanitary, state.ccElectrical, state.ccTelecom].filter(Boolean).join(',');
    const sample = [all, cc].filter(Boolean).join(',');
    if (!sample) return true; // brak maili też OK
    return emailRegex.test(sample);
  }, [state]);

  const handleChange = (key: keyof KzmSettingsState, sub?: keyof Person) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setState(prev => {
      if (sub) {
        return { ...prev, [key]: { ...(prev[key] as Person), [sub]: value } } as KzmSettingsState;
      }
      return { ...prev, [key]: value } as KzmSettingsState;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setSuccess(null);
    setError(null);
    try {
      const toDto: Omit<KzmSettingsDto, 'project_id'> = {
        ordering_party_name: state.orderingParty.name,
        ordering_party_email: state.orderingParty.email,
        inspector_build_name: state.inspectorBuild.name,
        inspector_build_email: state.inspectorBuild.email,
        inspector_sanitary_name: state.inspectorSanitary.name,
        inspector_sanitary_email: state.inspectorSanitary.email,
        inspector_electrical_name: state.inspectorElectrical.name,
        inspector_electrical_email: state.inspectorElectrical.email,
        inspector_telecom_name: state.inspectorTelecom.name,
        inspector_telecom_email: state.inspectorTelecom.email,
        cc_build: state.ccBuild,
        cc_sanitary: state.ccSanitary,
        cc_electrical: state.ccElectrical,
        cc_telecom: state.ccTelecom,
        template_link: state.templateLink || null
      };
      await kzmSettingsService.upsertKzmSettings(projectId, toDto);
      setSuccess('Zapisano ustawienia KZM');
    } catch (e: any) {
      setError(e?.message || 'Nie udało się zapisać ustawień');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper sx={{ p: 2, border: '1px solid #eee' }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>Ustawienia KZM</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Uzupełnij odbiorców i DW dla wysyłek KZM w ramach projektu.</Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Grid container spacing={2}>
        <Grid item xs={12} md={12}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>Link do szablonu KZM</Typography>
          <TextField fullWidth label="URL dokumentu (Google Docs)" value={state.templateLink} onChange={handleChange('templateLink' as any)} placeholder="https://docs.google.com/document/d/..." />
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>Zamawiający</Typography>
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Imię i nazwisko" value={state.orderingParty.name} onChange={handleChange('orderingParty', 'name')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Adres e-mail" value={state.orderingParty.email} onChange={handleChange('orderingParty', 'email')} error={!!state.orderingParty.email && !emailRegex.test(state.orderingParty.email)} />
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>Inspektor nadzoru budowlanego</Typography>
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Imię i nazwisko" value={state.inspectorBuild.name} onChange={handleChange('inspectorBuild', 'name')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Adres e-mail" value={state.inspectorBuild.email} onChange={handleChange('inspectorBuild', 'email')} error={!!state.inspectorBuild.email && !emailRegex.test(state.inspectorBuild.email)} />
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>Inspektor nadzoru sanitarnej</Typography>
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Imię i nazwisko" value={state.inspectorSanitary.name} onChange={handleChange('inspectorSanitary', 'name')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Adres e-mail" value={state.inspectorSanitary.email} onChange={handleChange('inspectorSanitary', 'email')} error={!!state.inspectorSanitary.email && !emailRegex.test(state.inspectorSanitary.email)} />
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>Inspektor nadzoru elektrycznej</Typography>
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Imię i nazwisko" value={state.inspectorElectrical.name} onChange={handleChange('inspectorElectrical', 'name')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Adres e-mail" value={state.inspectorElectrical.email} onChange={handleChange('inspectorElectrical', 'email')} error={!!state.inspectorElectrical.email && !emailRegex.test(state.inspectorElectrical.email)} />
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>Inspektor nadzoru teletechnicznej</Typography>
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Imię i nazwisko" value={state.inspectorTelecom.name} onChange={handleChange('inspectorTelecom', 'name')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Adres e-mail" value={state.inspectorTelecom.email} onChange={handleChange('inspectorTelecom', 'email')} error={!!state.inspectorTelecom.email && !emailRegex.test(state.inspectorTelecom.email)} />
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 1 }} />
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>Wysyłanie KZM — DW</Typography>
          <Grid container spacing={1.5}>
            <Grid item xs={12}>
              <TextField fullWidth label="Branża budowlana — e‑maile po przecinku" value={state.ccBuild} onChange={handleChange('ccBuild')} placeholder="email1@domena.pl, email2@domena.pl" error={!!state.ccBuild && !emailRegex.test(state.ccBuild)} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Branża sanitarna — e‑maile po przecinku" value={state.ccSanitary} onChange={handleChange('ccSanitary')} placeholder="email1@domena.pl, email2@domena.pl" error={!!state.ccSanitary && !emailRegex.test(state.ccSanitary)} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Branża elektryczna — e‑maile po przecinku" value={state.ccElectrical} onChange={handleChange('ccElectrical')} placeholder="email1@domena.pl, email2@domena.pl" error={!!state.ccElectrical && !emailRegex.test(state.ccElectrical)} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Branża teletechniczna — e‑maile po przecinku" value={state.ccTelecom} onChange={handleChange('ccTelecom')} placeholder="email1@domena.pl, email2@domena.pl" error={!!state.ccTelecom && !emailRegex.test(state.ccTelecom)} />
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button variant="outlined" onClick={() => setState(parseInitial(initialJson))} disabled={saving}>Reset</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || !isEmailsValid} sx={{ backgroundColor: '#004273', '&:hover': { backgroundColor: '#003660' } }}>
          {saving ? 'Zapisywanie…' : 'Zapisz ustawienia'}
        </Button>
      </Box>
    </Paper>
  );
};

export default KzmSettings;


